/**
 * @fileoverview Legal Page Layout
 *
 * Purpose: Shared chrome (nav, hero, prose container, footer) for the public
 *          legal pages (Terms of Service, Privacy Policy). Renders supplied
 *          legal copy as styled prose so the content is professional and
 *          self-hosted rather than embedded from a third-party Notion page.
 *
 * Used by: routes/terms.tsx, routes/privacy.tsx
 */

import type { ReactNode } from "react";
import { useAuth } from "@workos-inc/authkit-react";
import { Route as AuthLoginRoute } from "@/routes/_app/login/_layout.index";
import { Route as DashboardRoute } from "@/routes/_app/_auth/dashboard/_layout.index";
import { ScrollingNav } from "./-ui.scrolling-nav";
import { FooterSection } from "./-sections.footer";
import { CookieBanner } from "./-ui.cookie-banner";

interface LegalLayoutProps {
  /** Page title shown in the hero (e.g. "Terms of Service") */
  title: string;
  /** Last-updated / effective line shown beneath the title */
  lastUpdated: string;
  /** Rendered legal copy */
  children: ReactNode;
}

/**
 * Shared layout for public legal pages.
 *
 * @example
 * <LegalLayout title="Privacy Policy" lastUpdated="Last updated: 6 April 2026">
 *   <h2>1. Introduction</h2>
 *   ...
 * </LegalLayout>
 */
export function LegalLayout({
  title,
  lastUpdated,
  children,
}: LegalLayoutProps) {
  const { user, isLoading } = useAuth();

  return (
    <div className="relative w-full">
      <ScrollingNav
        isAuthenticated={!!user}
        isLoading={isLoading}
        loginPath={AuthLoginRoute.fullPath}
        dashboardPath={DashboardRoute.fullPath}
      />

      {/* Hero - pulled up behind the sticky nav so the white logo sits on the dark background */}
      <section className="relative -mt-[65px] overflow-hidden bg-[#1a4025] pb-16 pt-[calc(6rem_+_65px)]">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <h1 className="text-4xl font-medium tracking-tight text-white md:text-5xl">
            {title}
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-base text-white/60">
            {lastUpdated}
          </p>
        </div>
      </section>

      {/* Legal copy */}
      <section className="w-full bg-background py-16">
        <div className="mx-auto max-w-4xl px-6">
          <article className="prose prose-neutral max-w-none prose-headings:scroll-mt-24 prose-headings:font-semibold prose-headings:tracking-tight prose-h2:mb-4 prose-h2:mt-12 prose-h2:text-2xl prose-h2:text-foreground prose-h3:mb-3 prose-h3:mt-8 prose-h3:text-lg prose-h3:text-foreground prose-p:text-[15px] prose-p:leading-relaxed prose-p:text-muted-foreground prose-a:font-medium prose-a:text-primary hover:prose-a:text-primary/80 prose-strong:font-semibold prose-strong:text-foreground prose-li:text-[15px] prose-li:text-muted-foreground prose-table:text-sm prose-th:text-left prose-th:font-semibold prose-th:text-foreground prose-td:align-top prose-td:text-muted-foreground">
            {children}
          </article>
        </div>
      </section>

      <FooterSection />
      <CookieBanner />
    </div>
  );
}
